<template>
  <VChart
    ref="chart"
    v-bind="$attrs"
    autoresize
    style="width: 100%; height: 100%; min-height: 400px;"
  />
</template>

<script setup lang="ts">
import { useTemplateRef, onUnmounted, ref, watch } from 'vue'
import VChart from 'vue-echarts'
import { use, registerMap } from 'echarts/core'
import { CanvasRenderer } from 'echarts/renderers'
import { BarChart, LineChart, ScatterChart, PieChart, SunburstChart, MapChart } from 'echarts/charts'
import {
  LegendComponent,
  LegendScrollComponent,
  TooltipComponent,
  VisualMapComponent,
  GeoComponent,
  TitleComponent,
  GridComponent
} from 'echarts/components'

import logger from '@/utils/logger'

// Register only the charts and components we use (tree-shaking)
use([
  CanvasRenderer,
  BarChart, LineChart, ScatterChart, PieChart, SunburstChart, MapChart,
  LegendComponent, LegendScrollComponent, TooltipComponent,
  VisualMapComponent, GeoComponent, TitleComponent, GridComponent
])

// Register our map data
import WorldData from '@geo-maps/countries-coastline-10km/map.geo.json';
import { toPacificGeoJson } from '@/utils/pacificMap'

// Drop Antarctica
WorldData.features = WorldData.features.filter(f => f.properties.A3 !== 'ATA')

// ECharts 5 requires properties.name on each feature for geo regions
WorldData.features.forEach(f => {
  if (!(f.properties as any).name) (f.properties as any).name = f.properties.A3
})

// Creates map with the different country borders
const WorldCountriesData = {
  type: 'FeatureCollection',
  features: WorldData.features.map(
    // echart uses properties.name to differentiate shapes
    ({properties: {A3}, ...rest}) => ({...rest, properties: {name: A3}})
  )
}

// `world` stays Atlantic-centred (Accommodation map).
registerMap('world', WorldData as any)
// `worldPacific` — the same country geometry / full names as `world` but
// re-framed Pacific-centred (TAMK3-9 follow-on), used by the Summary
// destination map. Deep-copied inside toPacificGeoJson so `world` is untouched.
registerMap('worldPacific', toPacificGeoJson(WorldData) as any)
// `worldWithCountries` is used ONLY by the Travel Tracker map, which is
// Pacific-centred (TAMK3-9): shift the country geometry into the Pacific frame
// here (deep-copied, so the shared `world` geometry above is untouched). Kept
// inside this registerMap block — a previous separate-module registration was
// reverted because registerMap must run after use() has loaded MapChart /
// GeoComponent, and a separate module fought that ordering.
registerMap('worldWithCountries', toPacificGeoJson(WorldCountriesData) as any)

const LOG = logger('Chart')

defineOptions({ name: 'Chart', inheritAttrs: false })

const props = defineProps({
  beforePrint: {
    required: false,
    type: Function,
    default: null
  }
})

const chart = useTemplateRef('chart')
const ready = ref(false)
const pendingMerges: any[] = []

// Watch for VChart's internal ECharts instance to be created
watch(() => chart.value?.chart, (instance) => {
  if (instance && !ready.value) {
    // Defer to next macrotask so ECharts finishes its initial setOption
    setTimeout(() => {
      ready.value = true
      // Flush any queued mergeOptions calls
      while (pendingMerges.length) {
        const args = pendingMerges.shift()
        instance.setOption(args, { notMerge: false, lazyUpdate: true })
      }
    }, 50)
  }
})

function mergeOptions (args) {
  if (ready.value && chart.value?.chart) {
    chart.value.chart.setOption(args, { notMerge: false, lazyUpdate: true })
  } else {
    // Queue until chart is ready
    pendingMerges.push(args)
  }
}

// Pass-through to the underlying ECharts instance's
// dispatchAction. Used by callers that want to programmatically
// trigger select / unselect / etc. — e.g. the Traveller Tracker
// map keeps the clicked country highlighted while the modal is
// open via { type: 'mapSelect', ... }. No-op if the chart isn't
// ready yet (the caller is presumably reacting to a click that
// can only happen post-init).
function dispatchAction (action) {
  if (ready.value && chart.value?.chart) {
    chart.value.chart.dispatchAction(action)
  }
}

function prepare () {
  if (props.beforePrint) {
    props.beforePrint(chart.value)
    return
  }
  // Auto-calc print width as the chart card's proportion of its
  // outer row, scaled to a 1100px target (so a chart sitting in a
  // 7/12 column prints at ~640px). Originally targeted Bootstrap's
  // `.card` and `.row` classes — both have largely retired (`.card`
  // never existed on UiCard, and `.row` was replaced by `tw-grid`
  // in the PR-G4 page-grid sweep). Now searches for any of the
  // known chrome classes; falls back to a fixed 1100px width when
  // neither ancestor is found rather than crashing on a null
  // `.closest()` return.
  const $chart = chart.value?.$el
  if (!$chart) return
  const card = $chart.closest('.ui-card, .card')
  const row = $chart.closest('[class*="tw-grid"], .row')
  if (card && row && row.offsetWidth) {
    const width = 1100 * card.offsetWidth / row.offsetWidth
    resize({width, height: '400', silent: true})
  } else {
    // No detectable container — print at the standard target width.
    resize({width: 1100, height: '400', silent: true})
  }
}

function reset () {
  resize()
}

function resize (options?) {
  if (!ready.value || !chart.value?.chart) return
  LOG.debug(`[${chart.value.chart.id}] Resizing ${options || "auto"}`)
  chart.value.chart.resize(options || {width: 'auto', height: 'auto'})
}

function centerGeoMap (coordinates) {
  if (!ready.value || !chart.value?.chart) return
  const chartInstance = chart.value.chart
  const options = chartInstance.getOption()
  if (!options?.series || !(options.series as any[]).length || !(options.series as any[])[0]?.data) return
  const data = options.series[0].data
  // collect all lat and long
  const values = {'latitude': [], 'longitude': []}
  data.forEach(row => Object.keys(values).forEach(key => values[key].push(row[key])))

  // no data
  let zoom = 1
  let center = [0, 0]
  if (data.length > 1) {
    // calculate zoom (zoom-1 shows whole map [-180 90 180 -90])
    zoom = 1.0 / Math.max(
      (Math.max(...values.longitude) + (-1 * Math.min(...values.longitude)) + 5) / 360,
      (Math.max(...values.latitude) + (-1 * Math.min(...values.latitude)) + 5) / 180,
    )
    // calculate the center point
    center = [
      (Math.min(...values.longitude) + Math.max(...values.longitude)) / 2,
      (Math.min(...values.latitude) + Math.max(...values.latitude)) / 2,
    ]
  }

  LOG.debug(`[${chart.value.chart.id}] Center: ${center} Zoom: ${zoom}}`)
  chartInstance.setOption({geo: {zoom, center}})
}

// created lifecycle equivalent
window.addEventListener('beforeprint', prepare)
window.addEventListener('afterprint', reset)

onUnmounted(() => {
  window.removeEventListener('beforeprint', prepare)
  window.removeEventListener('afterprint', reset)
})

defineExpose({ mergeOptions, dispatchAction, centerGeoMap, prepare, reset, resize })
</script>
